import { makeStyles } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import ReactPaginateDefault from "react-paginate";
import { useSearchParams } from "react-router-dom";

// react-paginate is a CJS-only package; under Vite 8 / Rolldown the default
// import can resolve to a namespace object `{ default: Component }` instead
// of the component itself, depending on interop. Unwrap defensively.
const ReactPaginate = ((
  ReactPaginateDefault as unknown as { default?: typeof ReactPaginateDefault }
).default ?? ReactPaginateDefault) as typeof ReactPaginateDefault;

interface PaginateProps {
  pageCount: number;
  onPageClick: (page: number) => void;
  defaultPage?: number;
  disableQueryParameter?: boolean;
}

const useStyles = makeStyles({
  paginationComponent: {
    display: "flex",
    flexWrap: "wrap",
    minWidth: 0,
    maxWidth: "100%",
    listStyle: "none",
    margin: 0,
    padding: 0,
    "& > li": {
      display: "inline-block",
    },
    "& > li > a": {
      display: "inline-block",
      padding: "10px",
      minWidth: "30px",
      textAlign: "center",
      cursor: "pointer",
    },
    "& > li.selected": {
      fontWeight: "bolder",
      border: "1px solid #ddd",
    },
    "& > li.disabled > a": {
      color: "lightgrey",
      cursor: "default",
    },
  },
});

export const Paginate = (props: PaginateProps) => {
  const { pageCount, onPageClick, defaultPage, disableQueryParameter } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const page = defaultPage ? defaultPage.toString() : searchParams.get("page");
  const pageNumber = page ? parseInt(page) - 1 : 0;

  const handlePageClick = (targetPage: number) => {
    const newPage = targetPage + 1;

    if (!defaultPage && !disableQueryParameter) {
      if (newPage > 1) {
        searchParams.set("page", newPage.toString());
      } else {
        searchParams.delete("page");
      }

      setSearchParams(searchParams, {
        preventScrollReset: true,
      });
    }

    onPageClick(newPage);
  };

  return (
    <ReactPaginate
      className={classes.paginationComponent}
      breakLabel="..."
      nextLabel={t("paginateNext")}
      onPageChange={(item) => handlePageClick(item.selected)}
      pageRangeDisplayed={5}
      pageCount={pageCount}
      previousLabel={t("paginatePrevious")}
      renderOnZeroPageCount={null}
      forcePage={pageNumber}
    />
  );
};
